(() => {
  const configElement = document.getElementById('MagicMasteryUpsellConfig');
  const modal = document.getElementById('MagicMastery-Upsell-Modal');

  if (!configElement || !modal) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('Magic Mastery upsell configuration is invalid.', error);
    return;
  }

  if (!config.enabled && !config.designMode) return;

  const normalizeFlag = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/^_+|_+$/g, '');

  const stringId = (value) => value == null ? '' : String(value);

  const MMU_ANALYTICS_VERSION = '1';
  const MMU_ACCEPTED_TRIAL_STORAGE_KEY = 'dgm_mmu_accepted_trial_v1';
  const MMU_SUPPRESSION_STORAGE_VERSION = 2;
  const MMU_MEMBER_LAUNCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const MMU_CART_ATTRIBUTES = {
    attributionId: '__dgm_mmu_attribution_id',
    bonusHandle: '__dgm_mmu_bonus_handle',
    hasConditionalBonus: '__dgm_mmu_has_conditional_bonus',
    qualificationSource: '__dgm_mmu_qualification_source',
    acceptState: '__dgm_mmu_accept_state',
    version: '__dgm_mmu_version',
    addedProductId: '__dgm_mmu_added_product_id',
    addedVariantId: '__dgm_mmu_added_variant_id'
  };

  class MagicMasteryAnalytics {
    constructor(options) {
      this.config = options;
      this.presentation = null;
      this.cartAttributeWrite = Promise.resolve();
      this.acceptedTrial = this.readAcceptedTrial();
    }

    analyticsAllowed() {
      try {
        const privacy = window.Shopify && window.Shopify.customerPrivacy;
        return Boolean(privacy && typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed());
      } catch (error) {
        return false;
      }
    }

    analyticsAvailable() {
      return Boolean(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function');
    }

    createAttributionId() {
      try {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
      } catch (error) {}
      return `mmu-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    }

    start(context) {
      if (this.config.designMode || this.presentation) return this.presentation;

      const bonusHandle = normalizeFlag(context.bonusHandle || 'base').replace(/_/g, '-') || 'base';
      this.presentation = {
        attributionId: this.createAttributionId(),
        bonusHandle,
        hasConditionalBonus: bonusHandle !== 'base',
        qualificationSource: context.qualificationSource || 'base',
        addedProductId: stringId(context.addedProductId),
        addedVariantId: stringId(context.addedVariantId),
        active: this.analyticsAllowed() && this.analyticsAvailable(),
        viewed: false,
        accepted: false,
        dismissed: false
      };

      if (!this.presentation.active) return this.presentation;
      this.presentation.viewed = true;
      this.publish('mmu_upsell_viewed', this.eventPayload());
      this.persistCartAttributes('not_accepted');
      return this.presentation;
    }

    accept() {
      if (!this.presentation || !this.presentation.active || !this.presentation.viewed || this.presentation.accepted) return false;
      this.presentation.accepted = true;
      this.acceptedTrial = {
        attributionId: this.presentation.attributionId,
        bonusHandle: this.presentation.bonusHandle,
        hasConditionalBonus: this.presentation.hasConditionalBonus,
        qualificationSource: this.presentation.qualificationSource,
        addedProductId: this.presentation.addedProductId,
        addedVariantId: this.presentation.addedVariantId,
        offerVariantId: stringId(this.config.offerVariantId),
        sellingPlanId: stringId(this.config.sellingPlanId),
        acceptState: 'accepted',
        trialPresent: true,
        removalTracked: false,
        active: true
      };
      this.writeAcceptedTrial();
      this.publish('mmu_upsell_accepted', this.eventPayload({ mmu_accept_state: 'accepted' }));
      this.persistCartAttributes('accepted');
      return true;
    }

    dismiss(exitReason) {
      if (!this.presentation || !this.presentation.active || !this.presentation.viewed || this.presentation.accepted || this.presentation.dismissed) return false;
      this.presentation.dismissed = true;
      this.publish('mmu_upsell_dismissed', this.eventPayload({
        mmu_accept_state: 'not_accepted',
        mmu_exit_reason: exitReason || 'close'
      }));
      return true;
    }

    end() {
      this.presentation = null;
    }

    eventPayload(extra = {}) {
      if (!this.presentation) return extra;
      return this.contextPayload(this.presentation, extra);
    }

    contextPayload(context, extra = {}) {
      return {
        mmu_attribution_id: context.attributionId,
        mmu_bonus_handle: context.bonusHandle,
        mmu_has_conditional_bonus: context.hasConditionalBonus,
        mmu_qualification_source: context.qualificationSource,
        mmu_accept_state: context.acceptState || (context.accepted ? 'accepted' : 'not_accepted'),
        mmu_version: MMU_ANALYTICS_VERSION,
        mmu_added_product_id: context.addedProductId,
        mmu_added_variant_id: context.addedVariantId,
        ...extra
      };
    }

    publish(eventName, payload) {
      try {
        const result = window.Shopify.analytics.publish(eventName, payload);
        if (result && typeof result.catch === 'function') result.catch(() => {});
      } catch (error) {}
    }

    persistCartAttributes(acceptState, context = this.presentation) {
      if (!context || !context.active) return;
      const attributes = {
        [MMU_CART_ATTRIBUTES.attributionId]: context.attributionId,
        [MMU_CART_ATTRIBUTES.bonusHandle]: context.bonusHandle,
        [MMU_CART_ATTRIBUTES.hasConditionalBonus]: context.hasConditionalBonus ? 'true' : 'false',
        [MMU_CART_ATTRIBUTES.qualificationSource]: context.qualificationSource,
        [MMU_CART_ATTRIBUTES.acceptState]: acceptState,
        [MMU_CART_ATTRIBUTES.version]: MMU_ANALYTICS_VERSION,
        [MMU_CART_ATTRIBUTES.addedProductId]: context.addedProductId,
        [MMU_CART_ATTRIBUTES.addedVariantId]: context.addedVariantId
      };

      this.cartAttributeWrite = this.cartAttributeWrite
        .catch(() => {})
        .then(() => fetch(window.routes.cart_update_url || '/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes })
        }))
        .then((response) => {
          if (!response.ok) throw new Error('MMU cart attribution could not be saved.');
        })
        .catch(() => {});
    }

    readAcceptedTrial() {
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(MMU_ACCEPTED_TRIAL_STORAGE_KEY) || 'null');
        if (!stored || !stored.attributionId || !stored.bonusHandle) return null;
        if (stringId(stored.offerVariantId) !== stringId(this.config.offerVariantId)) return null;
        if (stringId(stored.sellingPlanId) !== stringId(this.config.sellingPlanId)) return null;
        return stored;
      } catch (error) {
        return null;
      }
    }

    writeAcceptedTrial() {
      try {
        if (this.acceptedTrial) {
          window.sessionStorage.setItem(MMU_ACCEPTED_TRIAL_STORAGE_KEY, JSON.stringify(this.acceptedTrial));
        }
      } catch (error) {}
    }

    observeCart(cart, isTrialLine) {
      if (!this.acceptedTrial || !cart || !Array.isArray(cart.items) || typeof isTrialLine !== 'function') return false;
      const hasTrackedTrial = cart.items.some((item) => Number(item.quantity || 0) > 0 && isTrialLine(item));
      const wasPresent = this.acceptedTrial.trialPresent === true;

      if (hasTrackedTrial) {
        if (!wasPresent && this.acceptedTrial.removalTracked) {
          this.acceptedTrial.acceptState = 'readded_after_removal';
          this.acceptedTrial.trialPresent = true;
          this.writeAcceptedTrial();
          this.persistCartAttributes('readded_after_removal', this.acceptedTrial);
        }
        return false;
      }

      if (!wasPresent) return false;

      this.acceptedTrial.trialPresent = false;
      this.acceptedTrial.acceptState = 'removed_after_accept';
      const shouldPublish = !this.acceptedTrial.removalTracked;
      this.acceptedTrial.removalTracked = true;
      this.writeAcceptedTrial();
      this.persistCartAttributes('removed_after_accept', this.acceptedTrial);

      if (shouldPublish) {
        this.publish('mmu_trial_removed', this.contextPayload(this.acceptedTrial, {
          mmu_accept_state: 'removed_after_accept'
        }));
      }
      return shouldPublish;
    }
  }

  class MagicMasteryUpsellController {
    constructor(options, modalElement) {
      this.config = options;
      this.modal = modalElement;
      this.modalBody = modalElement.querySelector('[data-mmu-modal-body]');
      this.trialWarningModal = document.getElementById('MagicMastery-Trial-Warning-Modal');
      this.cachedPageContent = null;
      this.currentContent = null;
      this.popupActive = false;
      this.previewActive = false;
      this.acceptPending = false;
      this.accepted = false;
      this.cartSurface = null;
      this.cartOpenOverride = null;
      this.opener = null;
      this.trialDependencyPending = false;
      this.trialDependencyCheckScheduled = false;
      this.essentialCartUpdateUnsubscribe = null;
      this.latestCart = null;
      this.pendingTrialRemoval = null;
      this.pendingAnalyticsContext = null;
      this.mediaFrameCount = 0;
      this.youtubePlayers = new Map();
      this.youtubeApiCallbacks = [];
      this.youtubeApiLoading = false;
      this.analytics = new MagicMasteryAnalytics(this.config);

      this.validFlags = new Set([normalizeFlag(this.config.memberFlag)]);
      (this.config.bonuses || []).forEach((bonus) => {
        (bonus.audienceFlags || []).forEach((flag) => this.validFlags.add(normalizeFlag(flag)));
      });
      this.validFlags.delete('');
      this.suppressionState = this.emptySuppressionState();
      this.audienceFlags = new Set();
      if (!this.config.designMode) {
        this.suppressionState = this.readSuppressionState();
        this.audienceFlags = this.captureAudienceFlags();
      }

      this.onBeforeCartPresentation = this.onBeforeCartPresentation.bind(this);
      this.onModalClosed = this.onModalClosed.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onSectionReload = this.onSectionReload.bind(this);
      this.onCartPotentiallyChanged = this.onCartPotentiallyChanged.bind(this);
      this.onPotentialCartRemoval = this.onPotentialCartRemoval.bind(this);
      this.onTrialWarningClick = this.onTrialWarningClick.bind(this);
      this.onUpsellMediaPlay = this.onUpsellMediaPlay.bind(this);
      this.onUpsellPlayerMessage = this.onUpsellPlayerMessage.bind(this);
      this.stopAllUpsellMedia = this.stopAllUpsellMedia.bind(this);
      this.onDelegatedProductCtaSubmit = this.onDelegatedProductCtaSubmit.bind(this);

      document.addEventListener('magicMasteryUpsell:beforeCartPresentation', this.onBeforeCartPresentation);
      document.addEventListener('submit', this.onDelegatedProductCtaSubmit, true);
      document.body.addEventListener('modalClosed', this.onModalClosed);
      this.modal.addEventListener('click', this.onModalClick);
      this.modal.addEventListener('play', this.onUpsellMediaPlay, true);
      window.addEventListener('message', this.onUpsellPlayerMessage);
      window.addEventListener('pagehide', this.stopAllUpsellMedia);
      document.addEventListener('afterCartChanged', this.onCartPotentiallyChanged);
      document.addEventListener('afterUpdateQuantity', this.onCartPotentiallyChanged);
      if (this.trialWarningModal) this.trialWarningModal.addEventListener('click', this.onTrialWarningClick);

      if (typeof this.modal.hide === 'function') {
        const hideModal = this.modal.hide.bind(this.modal);
        this.modal.hide = (...args) => {
          this.stopAllUpsellMedia();
          return hideModal(...args);
        };
      }

      if (!this.config.closeOnOverlay) {
        this.modal.addEventListener('click', (event) => {
          if (event.target === this.modal) event.stopImmediatePropagation();
        }, true);
      }

      if (this.config.designMode) {
        document.addEventListener('shopify:section:load', this.onSectionReload);
        document.addEventListener('shopify:section:select', this.onSectionReload);
        document.addEventListener('shopify:block:select', this.onSectionReload);
        window.setTimeout(() => this.openEditorPreview(document), 250);
      } else {
        document.addEventListener('click', this.onPotentialCartRemoval, true);
        this.bindEssentialCartUpdates();
        this.scheduleTrialDependencyCheck();
      }
    }

    onDelegatedProductCtaSubmit(event) {
      const form = event.target && typeof event.target.closest === 'function'
        ? event.target.closest('.tcp-action-form')
        : null;
      if (!form) return;

      const primaryForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');
      if (!primaryForm || primaryForm === form || typeof primaryForm.requestSubmit !== 'function') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      primaryForm.requestSubmit();
    }

    bindEssentialCartUpdates(attempt = 0) {
      const api = window.essentialCartApi;
      if (api && typeof api.onCartUpdate === 'function') {
        this.essentialCartUpdateUnsubscribe = api.onCartUpdate(() => this.scheduleTrialDependencyCheck());
        return;
      }
      if (attempt < 20) window.setTimeout(() => this.bindEssentialCartUpdates(attempt + 1), 250);
    }

    onCartPotentiallyChanged(event) {
      const cart = event && event.detail && event.detail.cartData;
      if (cart && Array.isArray(cart.items)) this.processConfirmedCart(cart);
      this.scheduleTrialDependencyCheck(cart);
    }

    processConfirmedCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return cart;
      this.latestCart = cart;
      this.analytics.observeCart(cart, (item) => this.isTrialLine(item));
      return cart;
    }

    onPotentialCartRemoval(event) {
      if (this.trialDependencyPending || this.pendingTrialRemoval || !this.latestCart) return;
      const target = event.target && typeof event.target.closest === 'function' ? event.target : null;
      if (!target) return;

      const removeControl = target.closest('cart-remove-button, [data-essential-cart-element="line-item-remove"]');
      const decrementControl = target.closest('[data-essential-cart-element="quantity-decrement"], button[name="minus"]');
      const control = removeControl || decrementControl;
      if (!control) return;
      if (decrementControl && !decrementControl.closest('#essential-cart-drawer-portal, cart-items, cart-drawer-items')) return;

      const line = this.resolveCartLineFromControl(control);
      if (!line || this.isTrialLine(line)) return;
      if (decrementControl && Number(line.quantity || 0) > 1) return;
      if (!this.removalWouldOrphanTrial(line, this.latestCart)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.pendingTrialRemoval = { lineKey: line.key, opener: control };
      this.showTrialDependencyWarning('confirm', control);
    }

    resolveCartLineFromControl(control) {
      const items = this.latestCart && Array.isArray(this.latestCart.items) ? this.latestCart.items : [];
      const nativeItem = control.closest('[id^="CartItem-"], [id^="CartDrawer-Item-"]');
      if (nativeItem) {
        const match = nativeItem.id.match(/(\d+)$/);
        if (match) return items[Number(match[1]) - 1] || null;
      }

      const label = control.getAttribute('aria-label') || '';
      return items.find((item) => label === `Remove ${item.product_title} from cart` || label === `Decrease quantity of ${item.product_title}`) || null;
    }

    removalWouldOrphanTrial(line, cart) {
      if (!line || !cart || !Array.isArray(cart.items) || this.isTrialLine(line)) return false;
      const activeItems = cart.items.filter((item) => Number(item.quantity || 0) > 0);
      const hasTrial = activeItems.some((item) => this.isTrialLine(item));
      const otherProducts = activeItems.filter((item) => !this.isTrialLine(item));
      return hasTrial && otherProducts.length === 1 && otherProducts[0].key === line.key;
    }

    scheduleTrialDependencyCheck(cart) {
      if (this.trialDependencyCheckScheduled || this.trialDependencyPending) return;
      this.trialDependencyCheckScheduled = true;
      window.setTimeout(() => {
        this.trialDependencyCheckScheduled = false;
        this.enforceTrialDependency(cart).catch((error) => {
          this.trialDependencyPending = false;
          console.error('Magic Mastery trial dependency could not be enforced.', error);
        });
      }, 100);
    }

    isTrialLine(item) {
      const variantMatches = stringId(item && (item.variant_id || item.id)) === stringId(this.config.offerVariantId);
      const allocation = item && item.selling_plan_allocation;
      const plan = allocation && allocation.selling_plan;
      return variantMatches && stringId(plan && plan.id) === stringId(this.config.sellingPlanId);
    }

    orphanedTrialLines(cart) {
      if (!cart || !Array.isArray(cart.items)) return [];
      const activeItems = cart.items.filter((item) => Number(item.quantity || 0) > 0);
      const trialLines = activeItems.filter((item) => this.isTrialLine(item));
      const hasAnotherProduct = activeItems.some((item) => !this.isTrialLine(item));
      return trialLines.length && !hasAnotherProduct ? trialLines : [];
    }

    async enforceTrialDependency(candidateCart) {
      if (this.config.designMode || this.trialDependencyPending) return;
      const cart = candidateCart && Array.isArray(candidateCart.items) ? candidateCart : await this.fetchCart();
      this.processConfirmedCart(cart);
      const trialLines = this.orphanedTrialLines(cart);
      if (!trialLines.length) return;

      this.trialDependencyPending = true;
      const updatedCart = await this.removeCartLines(trialLines);
      if (this.orphanedTrialLines(updatedCart).length) throw new Error('The dependent trial could not be removed.');

      this.refreshCartAfterTrialRemoval(updatedCart);
      this.trialDependencyPending = false;
      this.showTrialDependencyWarning('removed');
    }

    async removeCartLines(lines) {
      const sections = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];
      const updates = {};
      lines.forEach((item) => { updates[item.key] = 0; });
      const response = await fetch(window.routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections, sections_url: window.location.pathname })
      });
      const cart = await response.json();
      if (!response.ok || cart.errors) throw new Error(cart.description || cart.message || 'The cart could not be updated.');
      return this.processConfirmedCart(cart);
    }

    onTrialWarningClick(event) {
      const cancel = event.target.closest('[data-mmu-trial-cancel], [data-mmu-trial-dismiss]');
      if (cancel) {
        this.pendingTrialRemoval = null;
        this.trialWarningModal.hide();
        return;
      }
      if (event.target.closest('[data-mmu-trial-confirm]')) this.confirmPendingTrialRemoval();
    }

    async confirmPendingTrialRemoval() {
      if (!this.pendingTrialRemoval || this.trialDependencyPending) return;
      const pending = this.pendingTrialRemoval;
      const confirmButton = this.trialWarningModal.querySelector('[data-mmu-trial-confirm]');
      this.trialDependencyPending = true;
      if (confirmButton) confirmButton.disabled = true;

      try {
        const cart = await this.fetchCart();
        const selectedLine = cart.items.find((item) => item.key === pending.lineKey);
        if (!selectedLine) {
          this.pendingTrialRemoval = null;
          this.trialWarningModal.hide();
          return;
        }

        const activeItems = cart.items.filter((item) => Number(item.quantity || 0) > 0);
        const remainingProducts = activeItems.filter((item) => !this.isTrialLine(item) && item.key !== selectedLine.key);
        const linesToRemove = [selectedLine];
        if (!remainingProducts.length) linesToRemove.push(...activeItems.filter((item) => this.isTrialLine(item)));

        const updatedCart = await this.removeCartLines(linesToRemove);
        this.refreshCartAfterTrialRemoval(updatedCart);
        this.pendingTrialRemoval = null;
        this.trialWarningModal.hide();
      } catch (error) {
        const status = this.trialWarningModal.querySelector('[data-mmu-trial-warning-status]');
        if (status) {
          status.hidden = false;
          status.textContent = error.message || 'The cart could not be updated. Please try again.';
        }
      } finally {
        this.trialDependencyPending = false;
        if (confirmButton) confirmButton.disabled = false;
      }
    }

    refreshCartAfterTrialRemoval(cart) {
      const essentialApi = window.essentialCartApi;
      if (essentialApi && typeof essentialApi.refreshCart === 'function') essentialApi.refreshCart();

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && cart.sections && cart.sections['cart-drawer']) {
        cartDrawer.renderContents(cart, { open: false });
      }

      const cartPage = document.querySelector('cart-items:not(cart-drawer-items)');
      if (cartPage && typeof cartPage.renderContents === 'function' && cart.sections && cart.sections['main-cart-items']) {
        cartPage.renderContents(cart, null, null);
      }

      document.dispatchEvent(new CustomEvent('afterUpdateQuantity', { detail: {
        source: 'magic-mastery-trial-dependency',
        cartData: cart,
        variantId: this.config.offerVariantId
      }}));
    }

    showTrialDependencyWarning(mode = 'confirm', opener = document.activeElement) {
      if (!this.trialWarningModal || typeof this.trialWarningModal.show !== 'function') return;
      const confirming = mode === 'confirm';
      const heading = this.trialWarningModal.querySelector('[data-mmu-trial-warning-heading]');
      const message = this.trialWarningModal.querySelector('[data-mmu-trial-warning-message]');
      const confirmActions = this.trialWarningModal.querySelector('[data-mmu-trial-confirm-actions]');
      const dismissAction = this.trialWarningModal.querySelector('[data-mmu-trial-dismiss]');
      const status = this.trialWarningModal.querySelector('[data-mmu-trial-warning-status]');
      if (heading) heading.textContent = confirming ? heading.dataset.confirmText : heading.dataset.removedText;
      if (message) message.textContent = confirming ? message.dataset.confirmText : message.dataset.removedText;
      if (confirmActions) confirmActions.hidden = !confirming;
      if (dismissAction) dismissAction.hidden = confirming;
      if (status) {
        status.hidden = true;
        status.textContent = '';
      }
      if (!this.trialWarningModal.hasAttribute('open')) this.trialWarningModal.show(opener);
    }

    now() {
      return Date.now();
    }

    emptySuppressionState() {
      return {
        version: MMU_SUPPRESSION_STORAGE_VERSION,
        audience_flags: {}
      };
    }

    readSuppressionState() {
      const state = this.emptySuppressionState();
      const now = this.now();
      let shouldWrite = false;

      try {
        const stored = JSON.parse(window.localStorage.getItem(this.config.storageKey) || 'null');
        if (stored && Number(stored.version) === MMU_SUPPRESSION_STORAGE_VERSION) {
          if (stored.audience_flags && typeof stored.audience_flags === 'object') {
            Object.entries(stored.audience_flags).forEach(([rawFlag, entry]) => {
              const flag = normalizeFlag(rawFlag);
              if (!flag || !this.validFlags.has(flag)) {
                shouldWrite = true;
                return;
              }
              const capturedAt = Number(entry && entry.captured_at);
              const expiresAt = Number(entry && entry.expires_at);
              const normalizedCapturedAt = Number.isFinite(capturedAt) && capturedAt > 0 ? capturedAt : now;
              const isMemberFlag = flag === normalizeFlag(this.config.memberFlag);
              const normalizedEntry = {
                captured_at: normalizedCapturedAt,
                expires_at: isMemberFlag
                  ? normalizedCapturedAt + MMU_MEMBER_LAUNCH_TTL_MS
                  : (Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt : null)
              };
              if (isMemberFlag && normalizedEntry.expires_at !== expiresAt) shouldWrite = true;
              if (normalizedEntry.expires_at && normalizedEntry.expires_at <= now) {
                shouldWrite = true;
                return;
              }
              state.audience_flags[flag] = normalizedEntry;
            });
          }
          if (stored.mm_membership) shouldWrite = true;
        } else {
          const legacy = JSON.parse(window.localStorage.getItem(this.config.legacyStorageKey) || '[]');
          if (Array.isArray(legacy)) {
            legacy.map(normalizeFlag).filter((flag) => flag && this.validFlags.has(flag)).forEach((flag) => {
              state.audience_flags[flag] = {
                captured_at: now,
                expires_at: flag === normalizeFlag(this.config.memberFlag) ? now + MMU_MEMBER_LAUNCH_TTL_MS : null
              };
            });
            if (legacy.length) shouldWrite = true;
          }
          if (typeof window.localStorage.removeItem === 'function') window.localStorage.removeItem(this.config.legacyStorageKey);
        }
      } catch (error) {
        return state;
      }

      if (shouldWrite) this.writeSuppressionState(state);
      return state;
    }

    writeSuppressionState(state = this.suppressionState) {
      if (this.config.designMode) return;
      try {
        window.localStorage.setItem(this.config.storageKey, JSON.stringify(state));
      } catch (error) {
        console.warn('Magic Mastery suppression state could not be persisted.', error);
      }
    }

    captureAudienceFlags() {
      const currentUrl = new URL(window.location.href);
      const inboundValues = currentUrl.searchParams.getAll('dgm_audience');
      const now = this.now();
      let changed = false;

      inboundValues.flatMap((value) => value.split(',')).forEach((value) => {
        const flag = normalizeFlag(value);
        if (!flag || !this.validFlags.has(flag)) return;
        this.suppressionState.audience_flags[flag] = {
          captured_at: now,
          expires_at: flag === normalizeFlag(this.config.memberFlag) ? now + MMU_MEMBER_LAUNCH_TTL_MS : null
        };
        changed = true;
      });

      Object.entries(this.suppressionState.audience_flags).forEach(([flag, entry]) => {
        if (entry && entry.expires_at && Number(entry.expires_at) <= now) {
          delete this.suppressionState.audience_flags[flag];
          changed = true;
        }
      });

      if (inboundValues.length) {
        currentUrl.searchParams.delete('dgm_audience');
        window.history.replaceState(window.history.state, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
      }
      if (changed) this.writeSuppressionState();
      return new Set(Object.keys(this.suppressionState.audience_flags));
    }

    hasActiveMemberAudienceFlag() {
      const flag = normalizeFlag(this.config.memberFlag);
      const entry = this.suppressionState.audience_flags[flag];
      if (!entry) return false;
      if (entry.expires_at && Number(entry.expires_at) <= this.now()) {
        delete this.suppressionState.audience_flags[flag];
        this.audienceFlags.delete(flag);
        this.writeSuppressionState();
        return false;
      }
      return true;
    }

    isKnownMember() {
      if (Boolean(this.config.memberTagMatch)) return true;
      return this.hasActiveMemberAudienceFlag();
    }

    isMagicMasteryProduct(productId, variantId) {
      const normalizedProductId = stringId(productId);
      return normalizedProductId === stringId(this.config.magicMasteryProductId)
        || normalizedProductId === stringId(this.config.offerProductId)
        || stringId(variantId) === stringId(this.config.offerVariantId);
    }

    cartContainsMagicMastery(cart) {
      return Boolean(cart && Array.isArray(cart.items) && cart.items.some((item) => (
        this.isMagicMasteryProduct(item.product_id, item.variant_id || item.id)
      )));
    }

    onBeforeCartPresentation(event) {
      if (!this.config.enabled || this.config.designMode || this.popupActive || this.accepted) return;

      const detail = event.detail || {};
      const preAddCart = detail.preAddCart;
      if (!preAddCart || Number(preAddCart.item_count) !== 0) return;

      const addedVariantId = stringId(detail.productVariantId);
      const addedProductId = stringId(detail.cartData && detail.cartData.product_id);
      if (!addedVariantId || this.isMagicMasteryProduct(addedProductId, addedVariantId)) return;
      if (this.cartContainsMagicMastery(preAddCart) || this.isKnownMember()) return;
      if (!this.config.upsellPageUrl || !this.config.offerVariantId || !this.config.sellingPlanId) return;

      event.preventDefault();
      this.popupActive = true;
      this.cartSurface = detail.cartSurface || document.querySelector('cart-drawer, cart-notification');
      this.suppressCartPresentation();
      this.opener = detail.opener || document.activeElement;
      const winningBonus = this.chooseBonus(addedProductId);
      this.pendingAnalyticsContext = {
        bonusHandle: winningBonus && winningBonus.handle ? winningBonus.handle : 'base',
        qualificationSource: this.bonusQualificationSource(winningBonus, addedProductId),
        addedProductId,
        addedVariantId
      };

      const launch = async () => {
        try {
          const currentCart = await this.fetchCart();
          if (this.cartContainsMagicMastery(currentCart)) {
            this.popupActive = false;
            this.pendingAnalyticsContext = null;
            this.releaseCartPresentation();
            this.presentCart();
            return;
          }
          await this.openPopup(winningBonus);
        } catch (error) {
          this.handlePopupFailure(error);
        }
      };
      if (detail.quickAdd) {
        document.body.addEventListener('drawerClosed', launch, { once: true });
      } else {
        launch();
      }
    }

    chooseBonus(addedProductId) {
      const productId = stringId(addedProductId);
      return (this.config.bonuses || [])
        .filter((bonus) => {
          if (!bonus.enabled) return false;

          const triggerIds = (bonus.triggerProductIds || []).map(stringId);
          const flags = (bonus.audienceFlags || []).map(normalizeFlag).filter(Boolean);
          const hasProductConditions = triggerIds.length > 0;
          const hasCustomerConditions = Boolean(bonus.requiresCustomerTag);
          const hasAudienceConditions = flags.length > 0;
          const hasConditions = hasProductConditions || hasCustomerConditions || hasAudienceConditions;

          const productMatches = hasProductConditions && triggerIds.includes(productId);
          const customerMatches = hasCustomerConditions && Boolean(bonus.customerTagMatch);
          const audienceMatches = hasAudienceConditions && flags.some((flag) => this.audienceFlags.has(flag));

          return hasConditions && (productMatches || customerMatches || audienceMatches);
        })
        .sort((left, right) => Number(right.priority || 0) - Number(left.priority || 0) || String(left.handle).localeCompare(String(right.handle)))[0] || null;
    }

    bonusQualificationSource(bonus, addedProductId) {
      if (!bonus) return 'base';
      const sources = [];
      const productId = stringId(addedProductId);
      const triggerIds = (bonus.triggerProductIds || []).map(stringId);
      const flags = (bonus.audienceFlags || []).map(normalizeFlag).filter(Boolean);
      if (triggerIds.length && triggerIds.includes(productId)) sources.push('product');
      if (bonus.requiresCustomerTag && bonus.customerTagMatch) sources.push('customer_tag');
      if (flags.some((flag) => this.audienceFlags.has(flag))) sources.push('audience_flag');
      if (sources.length > 1) return 'multiple';
      return sources[0] || 'unknown';
    }

    async loadPageContent() {
      if (this.cachedPageContent) return this.cachedPageContent.cloneNode(true);

      const requestUrl = new URL(this.config.upsellPageUrl, window.location.origin);
      requestUrl.searchParams.set('view', 'magic-mastery-upsell');
      requestUrl.searchParams.set('mmu_source', 'modal');
      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Upsell page returned ${response.status}.`);

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const pageContent = parsedDocument.querySelector('[data-magic-mastery-upsell-page-content]');
      if (!pageContent) throw new Error('Upsell page content was not found. Confirm its assigned template.');

      this.cachedPageContent = pageContent.cloneNode(true);
      return this.cachedPageContent.cloneNode(true);
    }

    async openPopup(bonus) {
      this.setLoadingState();
      const content = await this.loadPageContent();
      this.mountContent(content, bonus);
      this.previewActive = false;
      this.modal.show(this.opener);
      if (this.modal.hasAttribute('open')) this.analytics.start(this.pendingAnalyticsContext || {
        bonusHandle: bonus && bonus.handle ? bonus.handle : 'base',
        qualificationSource: bonus ? 'unknown' : 'base',
        addedProductId: '',
        addedVariantId: ''
      });
    }

    setLoadingState() {
      this.modalBody.innerHTML = '<div class="magic-mastery-upsell-modal__loading" data-mmu-loading>Loading your special offer…</div>';
    }

    mountContent(content, bonus) {
      content.dataset.mmuMountedCopy = '';
      this.currentContent = content;
      const slot = content.querySelector('[data-mmu-bonus-slot]');
      if (slot) {
        slot.replaceChildren();
        if (bonus) slot.appendChild(this.buildBonus(bonus));
      }

      this.modalBody.replaceChildren(content);
      this.accepted = false;
      this.acceptPending = false;
      this.updateAcceptButtons('idle');
      window.pauseAllMedia();
      this.setupUpsellMedia();
    }

    buildBonus(bonus) {
      const container = document.createElement('section');
      container.className = 'magic-mastery-upsell-page__bonus';
      container.dataset.bonusHandle = bonus.handle || '';

      if (bonus.copyHtml) {
        const copy = document.createElement('div');
        copy.className = 'magic-mastery-upsell-page__bonus-copy rte';
        copy.innerHTML = bonus.copyHtml;
        container.appendChild(copy);
      }

      if (bonus.videoUrl) container.appendChild(this.buildBonusVideo(bonus.videoUrl, bonus.heading));

      if (bonus.imageUrl) {
        const image = document.createElement('img');
        image.src = bonus.imageUrl;
        image.alt = bonus.imageAlt || '';
        image.loading = 'lazy';
        container.appendChild(image);
      }

      return container;
    }

    buildBonusVideo(videoUrl, title) {
      const wrapper = document.createElement('div');
      wrapper.className = 'magic-mastery-upsell-page__video';

      let parsedUrl;
      try {
        parsedUrl = new URL(videoUrl, window.location.origin);
      } catch (error) {
        return wrapper;
      }

      const hostname = parsedUrl.hostname.toLowerCase();
      let embedUrl = '';
      if (hostname.includes('youtu.be')) {
        embedUrl = `https://www.youtube-nocookie.com/embed/${parsedUrl.pathname.split('/').filter(Boolean)[0] || ''}`;
      } else if (hostname.includes('youtube.com')) {
        embedUrl = `https://www.youtube-nocookie.com/embed/${parsedUrl.searchParams.get('v') || parsedUrl.pathname.split('/').filter(Boolean).pop() || ''}`;
      } else if (hostname === 'player.vimeo.com') {
        const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
        const videoIndex = pathParts.indexOf('video');
        if (videoIndex !== -1 && /^\d+$/.test(pathParts[videoIndex + 1] || '')) {
          embedUrl = parsedUrl.toString();
        }
      } else if (hostname === 'vimeo.com' || hostname.endsWith('.vimeo.com')) {
        const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
        let videoIndex = -1;
        for (let index = pathParts.length - 1; index >= 0; index -= 1) {
          if (/^\d+$/.test(pathParts[index])) {
            videoIndex = index;
            break;
          }
        }

        if (videoIndex !== -1) {
          const playerUrl = new URL(`https://player.vimeo.com/video/${pathParts[videoIndex]}`);
          parsedUrl.searchParams.forEach((value, key) => playerUrl.searchParams.append(key, value));

          const privacyHash = pathParts[videoIndex + 1] || '';
          if (privacyHash && !playerUrl.searchParams.has('h')) playerUrl.searchParams.set('h', privacyHash);
          embedUrl = playerUrl.toString();
        }
      }

      if (embedUrl) {
        const iframe = document.createElement('iframe');
        iframe.src = embedUrl;
        iframe.title = title || 'Bonus video';
        iframe.loading = 'lazy';
        iframe.allow = 'autoplay; fullscreen; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        wrapper.appendChild(iframe);
      } else {
        const video = document.createElement('video');
        video.src = parsedUrl.toString();
        video.controls = true;
        video.preload = 'none';
        wrapper.appendChild(video);
      }

      return wrapper;
    }

    mediaFrameUrl(frame) {
      return frame.getAttribute('src') || '';
    }

    isYoutubeFrame(frame) {
      const url = this.mediaFrameUrl(frame);
      return url.includes('youtube.com/embed/') || url.includes('youtube-nocookie.com/embed/');
    }

    isVimeoFrame(frame) {
      return this.mediaFrameUrl(frame).includes('player.vimeo.com/video/');
    }

    postToUpsellFrame(frame, message) {
      if (frame && frame.contentWindow) frame.contentWindow.postMessage(message, '*');
    }

    pauseUpsellMedia(activeMedia = null) {
      this.modal.querySelectorAll('video, audio').forEach((media) => {
        if (media !== activeMedia && !media.paused) media.pause();
      });

      this.modal.querySelectorAll('iframe').forEach((frame) => {
        if (frame === activeMedia) return;
        if (this.isVimeoFrame(frame)) this.postToUpsellFrame(frame, { method: 'pause' });
        if (this.isYoutubeFrame(frame)) {
          const player = this.youtubePlayers.get(frame);
          if (player && typeof player.pauseVideo === 'function') player.pauseVideo();
          else this.postToUpsellFrame(frame, JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }));
        }
      });
    }

    stopAllUpsellMedia() {
      this.pauseUpsellMedia(null);
      this.modal.querySelectorAll('video, audio').forEach((media) => {
        try { media.currentTime = 0; } catch (error) {}
      });

      // Removing an iframe source is the reliable final stop for cross-origin players,
      // including private Vimeo embeds that do not accept API commands.
      this.modal.querySelectorAll('iframe').forEach((frame) => {
        const source = frame.getAttribute('src');
        if (!source) return;
        frame.dataset.mmuStoppedSrc = source;
        frame.removeAttribute('src');
      });
    }

    onUpsellMediaPlay(event) {
      const media = event.target;
      if (!media || !this.modal.contains(media) || !media.matches('video, audio')) return;
      this.pauseUpsellMedia(media);
    }

    onUpsellPlayerMessage(event) {
      let data = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (error) { return; }
      }
      if (!data) return;

      const isVimeoPlay = data.event === 'play';
      const isYoutubePlay = data.event === 'onStateChange' && Number(data.info) === 1;
      if (!isVimeoPlay && !isYoutubePlay) return;

      const activeFrame = Array.from(this.modal.querySelectorAll('iframe')).find((frame) => frame.contentWindow === event.source);
      if (activeFrame) this.pauseUpsellMedia(activeFrame);
    }

    prepareUpsellFrame(frame) {
      if (frame.dataset.mmuMediaReady) return;
      let url;
      try {
        url = new URL(this.mediaFrameUrl(frame), window.location.href);
      } catch (error) {
        return;
      }

      if (!this.isVimeoFrame(frame) && !this.isYoutubeFrame(frame)) return;
      this.mediaFrameCount += 1;
      if (!frame.id) frame.id = `MagicMastery-Upsell-Video-${this.mediaFrameCount}`;
      frame.dataset.mmuMediaReady = 'true';

      if (this.isVimeoFrame(frame)) {
        url.searchParams.set('api', '1');
        url.searchParams.set('player_id', frame.id);
        frame.src = url.toString();
        const subscribe = () => this.postToUpsellFrame(frame, { method: 'addEventListener', value: 'play' });
        frame.addEventListener('load', subscribe);
        window.setTimeout(subscribe, 750);
        return;
      }

      url.searchParams.set('enablejsapi', '1');
      url.searchParams.set('origin', window.location.origin);
      frame.src = url.toString();
      this.loadYoutubeApi(() => {
        try {
          const player = new window.YT.Player(frame.id, {
            events: {
              onStateChange: (playerEvent) => {
                if (playerEvent.data === window.YT.PlayerState.PLAYING) this.pauseUpsellMedia(frame);
              }
            }
          });
          this.youtubePlayers.set(frame, player);
        } catch (error) {}
      });
    }

    setupUpsellMedia() {
      this.modal.querySelectorAll('iframe').forEach((frame) => this.prepareUpsellFrame(frame));
    }

    loadYoutubeApi(callback) {
      if (window.YT && window.YT.Player) {
        callback();
        return;
      }

      this.youtubeApiCallbacks.push(callback);
      if (this.youtubeApiLoading) return;
      this.youtubeApiLoading = true;
      const previousReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousReady === 'function') previousReady();
        this.youtubeApiCallbacks.splice(0).forEach((readyCallback) => readyCallback());
      };

      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(script);
      }
    }

    onModalClick(event) {
      const acceptButton = event.target.closest('[data-mmu-accept]');
      if (acceptButton) {
        event.preventDefault();
        this.acceptOffer();
        return;
      }

      if (event.target.closest('[data-mmu-continue]')) {
        event.preventDefault();
        this.analytics.dismiss('continue_shopping');
        window.location.assign(this.config.shopDestination || '/collections/all');
        return;
      }

      if (event.target.closest('[data-mmu-checkout]')) {
        event.preventDefault();
        this.analytics.dismiss('checkout');
        window.location.assign(this.config.checkoutUrl || '/checkout');
      }
    }

    async acceptOffer() {
      if (this.acceptPending || this.accepted || this.previewActive) return;

      this.acceptPending = true;
      this.updateAcceptButtons('loading');
      this.showStatus(this.pageMessage('addingMessage', 'Adding Magic Mastery…'), 'loading');

      try {
        const cart = await this.fetchCart();
        const existingLine = cart.items.find((item) => stringId(item.variant_id || item.id) === stringId(this.config.offerVariantId));
        if (existingLine) {
          const existingPlanId = stringId(existingLine.selling_plan_allocation && existingLine.selling_plan_allocation.selling_plan && existingLine.selling_plan_allocation.selling_plan.id);
          if (existingPlanId === stringId(this.config.sellingPlanId)) {
            this.markAccepted(this.pageMessage('alreadyMessage', 'Magic Mastery is already in your cart.'), false);
            return;
          }
          throw new Error('This offer product is already in the cart with a different purchase option.');
        }

        const request = fetchConfig('javascript');
        request.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete request.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', this.config.offerVariantId);
        formData.append('quantity', '1');
        formData.append('selling_plan', this.config.sellingPlanId);

        if (this.cartSurface && typeof this.cartSurface.getSectionsToRender === 'function') {
          formData.append('sections', this.cartSurface.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
        }
        request.body = formData;

        const response = await fetch(window.routes.cart_add_url, request);
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'The subscription could not be added.');

        const returnedVariantId = stringId(result.variant_id || result.id);
        const returnedProductId = stringId(result.product_id);
        const returnedPlanId = stringId(result.selling_plan_allocation && result.selling_plan_allocation.selling_plan && result.selling_plan_allocation.selling_plan.id);
        const productMatches = !this.config.offerProductId || returnedProductId === stringId(this.config.offerProductId);
        if (returnedVariantId !== stringId(this.config.offerVariantId) || returnedPlanId !== stringId(this.config.sellingPlanId) || !productMatches) {
          throw new Error('Shopify did not confirm the expected Magic Mastery selling plan.');
        }

        if (this.cartSurface && typeof this.cartSurface.renderContents === 'function' && result.sections) {
          this.cartSurface.renderContents(result, { open: false });
        }
        document.dispatchEvent(new CustomEvent('afterCartChanged', { detail: {
          productVariantId: this.config.offerVariantId,
          source: 'magic-mastery-upsell',
          cartData: result
        }}));

        this.markAccepted(this.pageMessage('addedMessage', 'Magic Mastery was added with the $1 trial subscription.'), true);
      } catch (error) {
        this.acceptPending = false;
        this.updateAcceptButtons('idle');
        this.showStatus(error.message || this.pageMessage('errorMessage', 'The subscription could not be added. Please try again.'), 'error');
      }
    }

    async fetchCart() {
      const response = await fetch(`${window.routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('The current cart could not be checked.');
      const cart = await response.json();
      return this.processConfirmedCart(cart);
    }

    markAccepted(message, trackAcceptance = false) {
      this.acceptPending = false;
      this.accepted = true;
      if (trackAcceptance) this.analytics.accept();
      this.updateAcceptButtons('success');
      this.showStatus(message, 'success');
      if (this.popupActive && typeof this.modal.hide === 'function') this.modal.hide();
    }

    updateAcceptButtons(state) {
      this.modal.querySelectorAll('[data-mmu-accept]').forEach((button) => {
        const label = button.querySelector('[data-mmu-accept-label]');
        button.disabled = state !== 'idle';
        button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
        if (!label) return;
        if (state === 'loading') label.textContent = this.pageMessage('loadingLabel', 'Adding…');
        else if (state === 'success') label.textContent = button.dataset.successLabel || 'Magic Mastery added';
        else label.textContent = button.dataset.defaultLabel || 'Add Magic Mastery';
      });
    }

    showStatus(message, state) {
      let status = this.modal.querySelector('[data-mmu-status]');
      if (!status) {
        status = document.createElement('p');
        status.className = 'magic-mastery-upsell-page__status';
        status.dataset.mmuStatus = '';
        status.setAttribute('role', 'status');
        const firstOffer = this.modal.querySelector('[data-mmu-accept]');
        if (firstOffer) firstOffer.insertAdjacentElement('afterend', status);
        else this.modalBody.prepend(status);
      }
      status.dataset.state = state;
      status.textContent = message;
    }

    pageMessage(key, fallback) {
      return (this.currentContent && this.currentContent.dataset && this.currentContent.dataset[key]) || fallback;
    }

    onModalClosed() {
      window.setTimeout(() => {
        if (this.trialWarningModal && !this.trialWarningModal.hasAttribute('open') && !this.trialDependencyPending) {
          this.pendingTrialRemoval = null;
        }
      }, 0);
      if (!this.popupActive) return;
      this.stopAllUpsellMedia();
      if (!this.accepted) this.analytics.dismiss('close');
      this.popupActive = false;
      this.acceptPending = false;
      this.releaseCartPresentation();
      window.setTimeout(() => this.presentCart(), 0);
      this.analytics.end();
    }

    suppressCartPresentation() {
      document.body.classList.add('magic-mastery-upsell-active');
      if (this.cartOpenOverride || !this.cartSurface || typeof this.cartSurface.open !== 'function') return;

      const surface = this.cartSurface;
      const originalOpen = surface.open;
      const guardedOpen = (...args) => {
        if (this.popupActive) return;
        return originalOpen.apply(surface, args);
      };

      this.cartOpenOverride = { surface, originalOpen, guardedOpen };
      surface.open = guardedOpen;
    }

    releaseCartPresentation() {
      document.body.classList.remove('magic-mastery-upsell-active');
      if (!this.cartOpenOverride) return;

      const { surface, originalOpen, guardedOpen } = this.cartOpenOverride;
      if (surface.open === guardedOpen) surface.open = originalOpen;
      this.cartOpenOverride = null;
    }

    presentCart() {
      if (this.cartSurface && typeof this.cartSurface.open === 'function') this.cartSurface.open(this.opener);
    }

    handlePopupFailure(error) {
      console.error('Magic Mastery upsell could not be displayed.', error);
      this.popupActive = false;
      this.previewActive = false;
      this.pendingAnalyticsContext = null;
      this.analytics.end();
      this.releaseCartPresentation();
      this.presentCart();
    }

    findEditorSource(root, event) {
      const selector = '[data-magic-mastery-upsell-page-content]:not([data-mmu-mounted-copy])';
      const sectionId = event && event.detail && event.detail.sectionId;
      if (sectionId) {
        const section = document.getElementById(`shopify-section-${sectionId}`);
        const source = section && section.querySelector(selector);
        if (source) return source;
      }

      if (root && typeof root.matches === 'function' && root.matches(selector)) return root;
      if (root && typeof root.closest === 'function') {
        const source = root.closest(selector);
        if (source) return source;
      }
      if (root && typeof root.querySelector === 'function') {
        const source = root.querySelector(selector);
        if (source) return source;
      }
      return document.querySelector(selector);
    }

    openEditorPreview(root, event) {
      if (!this.config.designMode) return;
      const source = this.findEditorSource(root, event);
      if (!source) return;

      this.cachedPageContent = null;
      const previewHandle = normalizeFlag(source.dataset.previewBonusHandle).replace(/_/g, '-');
      const previewBonus = (this.config.bonuses || []).find((bonus) => String(bonus.handle) === previewHandle) || null;
      this.mountContent(source, previewBonus);
      this.previewActive = true;
      if (!this.modal.hasAttribute('open')) this.modal.show(source);
    }

    onSectionReload(event) {
      window.setTimeout(() => this.openEditorPreview(event.target || document, event), 0);
    }
  }

  window.magicMasteryUpsell = new MagicMasteryUpsellController(config, modal);
})();
